import  { useState, useRef, useEffect } from "react";
import dayjs from "dayjs";
import "../../../css/Inputs/AMLInput1.css";
import { ReactComponent as CalendarIcon } from "../../../assets/icons/calender.svg";

const AMLDateInput = ({
  label = "",
  value,
  onChange,
  isRequired = false,
  isDisabled = false,
  isError = false,
  readOnly = false,
  dateFormat = "DD/MM/YYYY",
  min,
  max,
}) => {
  const [status, setStatus] = useState("default");
  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef(null);
  const hiddenDateRef = useRef(null);
  const [hasFocus, setHasFocus] = useState(false);
  // --- sync external value -> local formatted text ---
  useEffect(() => {
    if (!value) return setInputValue("");
    const parsed = dayjs(value, [dateFormat, "YYYY-MM-DD"], true);
    setInputValue(parsed.isValid() ? parsed.format(dateFormat) : value);
  }, [value, dateFormat]);

  // Max length for numeric entry (no slashes) before we inject separators
  const maxLength =
    dateFormat === "YYYY" ? 4 : dateFormat === "MM/YYYY" ? 6 : 8;

  const formatValue = (raw) => {
    if (dateFormat === "YYYY") {
      if (raw.length === 2) return `20${raw}`; // e.g. "25" -> "2025"
      return raw.slice(0, 4);
    }
    if (dateFormat === "MM/YYYY") {
      if (raw.length >= 3) return `${raw.slice(0, 2)}/${raw.slice(2, 6)}`;
      return raw.slice(0, 2);
    }
    // DD/MM/YYYY
    if (raw.length >= 5)
      return `${raw.slice(0, 2)}/${raw.slice(2, 4)}/${raw.slice(4, 8)}`;
    if (raw.length >= 3) return `${raw.slice(0, 2)}/${raw.slice(2, 4)}`;
    return raw.slice(0, 2);
  };

  const handleInputChange = (e) => {
    const rawDigits = e.target.value.replace(/[^0-9]/g, "").slice(0, maxLength);
    const formatted = formatValue(rawDigits);
    setInputValue(formatted);

    const parsed = dayjs(formatted, dateFormat, true);
    onChange?.(parsed.isValid() ? parsed.format(dateFormat) : formatted);

    // keep caret at end
    const input = inputRef.current;
    setTimeout(
      () => input?.setSelectionRange(formatted.length, formatted.length),
      0
    );
  };

  const handleNativeChange = (e) => {
    const raw = e.target.value; // "YYYY-MM-DD" | "YYYY-MM" | "YYYY"
    let out = "";
    if (dayjs(raw).isValid()) out = dayjs(raw).format(dateFormat);
    setInputValue(out);
    onChange?.(out);
    setStatus("selected");
  };

  const getInputType = () =>
    dateFormat === "MM/YYYY"
      ? "month"
      : dateFormat === "YYYY"
      ? "number"
      : "date";

  const getNativeValue = () => {
    if (!value) return "";
    if (dateFormat === "MM/YYYY") {
      const [mm, yy] = value.split("/");
      return `${yy}-${mm}`;
    }
    if (dateFormat === "YYYY") return `${value}`;
    return dayjs(value, "DD/MM/YYYY", true).format("YYYY-MM-DD");
  };

  const placeholder =
    dateFormat === "MM/YYYY"
      ? "09/2025"
      : dateFormat === "YYYY"
      ? "2025"
      : "11/09/2025";

  const hasErrorText = typeof isError === "string" && isError.length > 0;

  return (
    <div className="AML-Input-Container">
      {label && (
        <div className="AML-Input-Label-Container">
          <div className="AML-Text-Body">{label}</div>
          {isRequired && (
            <div className="AML-Text-Body Required-Indicator">*</div>
          )}
        </div>
      )}

      <div
        onFocusCapture={() => setHasFocus(true)}
        className={`AML-Input-Icon-Container AML-Input1 input-container ${
          isDisabled ? "disabled" : ""
        } ${hasFocus ? "isFocused" : ""} ${status} ${
          isError ? "has-error" : ""
        } ${readOnly ? "readOnly" : ""}`}
      >
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          placeholder={placeholder}
          className="AML-Text-Body"
          onFocus={() => !readOnly && setStatus("selected")}
          onBlur={() => setStatus("default")}
          onChange={handleInputChange}
          disabled={isDisabled}
          readOnly={readOnly}
          aria-invalid={Boolean(isError)}
          aria-required={isRequired}
        />

        {/* Hidden native picker to leverage mobile/desktop date UI */}
        <input
          ref={hiddenDateRef}
          type={getInputType()}
          value={getNativeValue()}
          onChange={handleNativeChange}
          readOnly={readOnly}
          min={min}
          max={max}
          style={{ position: "absolute", opacity: 0, pointerEvents: "none" }}
        />

        {!readOnly && (
          <div
            className="input-icon-container calendar-icon"
            onClick={() => {
              if (!readOnly && !isDisabled)
                hiddenDateRef.current?.showPicker?.();
            }}
            role="button"
            aria-label="Open calendar"
          >
            <CalendarIcon />
          </div>
        )}
      </div>

      {/* {hasErrorText && (
        <div className="AML-Input-Helper is-error">{isError}</div>
      )} */}
    </div>
  );
};

export default AMLDateInput;
